import React, { useContext, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Link,
    Modal,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import AuthContext from '../context/AuthContext';
import Logo from '../assets/logo.png';


const gymBackend = (process.env.REACT_APP_GYM_BACKEND_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');


const getResponseMessage = (data, fallback) => {
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.error === 'string') return data.error;

    if (data && typeof data === 'object') {
        const firstValue = Object.values(data)[0];
        if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    }
    return fallback;
};


const Login = () => {
    const { loginUser, loginStatus } = useContext(AuthContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetResult, setResetResult] = useState({ severity: 'info', message: '' });

    const handleSubmit = async (event) => {
        setIsSubmitting(true);
        try {
            await loginUser(event);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetRequest = async (event) => {
        event.preventDefault();
        setResetLoading(true);
        setResetResult({ severity: 'info', message: '' });

        try {
            const response = await fetch(`${gymBackend}/verification/request-password-reset/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim().toLowerCase() }),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setResetResult({
                    severity: 'error',
                    message: getResponseMessage(data, "L'e-mail n'a pas pu être envoyé."),
                });
                return;
            }

            setResetResult({
                severity: 'success',
                message: getResponseMessage(
                    data,
                    "Si un compte correspond à cette adresse, un lien vient d'être envoyé.",
                ),
            });
        } catch (error) {
            setResetResult({
                severity: 'error',
                message: 'Le serveur est inaccessible. Veuillez réessayer.',
            });
        } finally {
            setResetLoading(false);
        }
    };

    const closeResetModal = () => {
        setModalOpen(false);
        setEmail('');
        setResetResult({ severity: 'info', message: '' });
    };

    return (
        <>
            <Paper
                elevation={4}
                sx={{
                    mt: 8,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 4,
                    width: '100%',
                    maxWidth: 420,
                    mx: 'auto',
                    textAlign: 'center',
                    direction: 'ltr',
                }}
            >
                <Box mb={3}>
                    <img src={Logo} alt="Gyma" style={{ width: '80px', height: 'auto' }} />
                </Box>

                <Typography variant="h5" fontWeight={700} gutterBottom>
                    Connexion à votre espace
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    <Stack spacing={2}>
                        <TextField
                            name="username"
                            type="email"
                            label="Adresse e-mail"
                            fullWidth
                            required
                            autoComplete="email"
                        />
                        <TextField
                            name="password"
                            label="Mot de passe"
                            type="password"
                            fullWidth
                            required
                            autoComplete="current-password"
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || loginStatus.loading}
                            sx={{ borderRadius: 2.5, py: 1.3, bgcolor: '#010449' }}
                        >
                            {isSubmitting || loginStatus.loading
                                ? <CircularProgress size={24} color="inherit" />
                                : 'Se connecter'}
                        </Button>
                    </Stack>
                </Box>

                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    sx={{ mt: 2, fontSize: 14 }}
                    onClick={() => setModalOpen(true)}
                >
                    Mot de passe oublié ?
                </Link>

                <Typography variant="body2" sx={{ mt: 1.5 }}>
                    Vous n'avez pas encore de compte ?{' '}
                    <Link href="/signup" underline="hover">Créer un compte</Link>
                </Typography>
            </Paper>

            <Snackbar
                open={Boolean(loginStatus.message)}
                autoHideDuration={5000}
                onClose={loginStatus.reset}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={loginStatus.reset}
                    severity={loginStatus.success ? 'success' : 'error'}
                    sx={{ width: '100%' }}
                >
                    {loginStatus.message}
                </Alert>
            </Snackbar>

            <Modal open={modalOpen} onClose={closeResetModal}>
                <Box
                    component="form"
                    onSubmit={handleResetRequest}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        p: { xs: 3, sm: 4 },
                        borderRadius: 4,
                        width: 'calc(100% - 32px)',
                        maxWidth: 460,
                        boxShadow: 24,
                        direction: 'ltr',
                    }}
                >
                    <Typography variant="h5" fontWeight={700} textAlign="center">
                        Réinitialiser le mot de passe
                    </Typography>
                    <Typography color="text.secondary" textAlign="center" sx={{ mt: 1, mb: 3 }}>
                        Saisissez l'adresse e-mail du responsable. Nous vous enverrons un lien valable pendant une heure.
                    </Typography>

                    {resetResult.message && (
                        <Alert severity={resetResult.severity} sx={{ mb: 2 }}>
                            {resetResult.message}
                        </Alert>
                    )}

                    <TextField
                        label="Adresse e-mail"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        fullWidth
                        required
                        autoComplete="email"
                        disabled={resetLoading}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                        <Button onClick={closeResetModal} color="inherit" fullWidth>
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={resetLoading}
                            sx={{ bgcolor: '#010449' }}
                        >
                            {resetLoading
                                ? <CircularProgress size={22} color="inherit" />
                                : 'Envoyer le lien'}
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default Login;
